import * as React from "react";
import { useQueryClient, type QueryKey } from "@tanstack/react-query";
import { socket, type Handler, type SocketEvent } from "./socket";

/** Subscribe to one blackhole binding for the life of the component. */
export function useSocketBinding(accountId: string | undefined, binding: string, handler: Handler) {
  const ref = React.useRef(handler);
  ref.current = handler;
  React.useEffect(() => {
    if (!accountId) return;
    return socket.bind(accountId, binding, (ev) => ref.current(ev));
  }, [accountId, binding]);
}

/**
 * Only the types in blackhole's `allowed_doc_types` (kazoo_documents
 * doc_types.hrl) can be subscribed to; phone numbers and port requests
 * live in other databases and are refused, so those screens rely on
 * refetching instead. `queue` is not one of them either: the call center
 * screens refetch after their own writes and take live state from the
 * `callcenter.*` family instead.
 */
export type DocType = "account" | "callflow" | "device" | "faxbox" | "media" | "user" | "vmbox" | "fax" | "mailbox_message" | "call_recording";

/**
 * Query-key prefixes each document type feeds. Screens key their queries
 * by these nouns, so a change to a document type refreshes every list and
 * detail that depends on it without each screen listing them by hand.
 */
export const DOC_KEYS: Record<DocType, string[]> = {
  account: ["account", "accounts", "limits", "security"],
  callflow: ["callflows", "callflow", "feature-codes", "main-user-callflows", "groups", "group", "used-by"],
  device: ["devices", "device", "devices-status", "registrations"],
  faxbox: ["faxboxes", "faxbox"],
  media: ["media", "medium"],
  user: ["users", "user", "directories", "directory"],
  vmbox: ["vmboxes", "vmbox", "vmbox-summaries"],
  fax: ["faxes", "fax"],
  mailbox_message: ["vmbox-messages", "vmbox-summaries"],
  call_recording: ["recordings"],
};

/**
 * Invalidate queries when documents of a type change. Realtime is a hint
 * layered over normal refetching, so this only ever calls invalidate.
 * Without `keys`, the DOC_KEYS table for the type is used.
 */
export function useDocInvalidation(accountId: string | undefined, type: DocType, keys?: QueryKey[] | ((ev: SocketEvent) => QueryKey[])) {
  const qc = useQueryClient();
  const invalidate = React.useCallback(
    (ev: SocketEvent) => {
      const list = keys === undefined ? DOC_KEYS[type].map((noun) => [noun, accountId]) : typeof keys === "function" ? keys(ev) : keys;
      for (const key of list) void qc.invalidateQueries({ queryKey: key });
    },
    [qc, keys, type, accountId],
  );
  useSocketBinding(accountId, `object.doc_created.${type}`, invalidate);
  useSocketBinding(accountId, `object.doc_edited.${type}`, invalidate);
  useSocketBinding(accountId, `object.doc_deleted.${type}`, invalidate);
}
